<template>
  <div class="relative flex py-5 items-center">
    <div class="flex-grow border-t border-gray-400"></div>
    <span class="flex-shrink mx-4 text-3xl font-bold">Upload Dokumen</span>
    <div class="flex-grow border-t border-gray-400"></div>
  </div>

  <div class="space-y-4">
    <div class="border-b border-gray-900/10 pb-12 px-8 py-20 mt-4 shadow-md rounded-xl bg-white">
      <h2 class="text-base text-gray-800 font-medium font-sans">Pemberkasan</h2>
      <p class="text-sm text-gray-500 font-medium pb-6 border-b">Upload berkas berikut</p>

      <div class="py-6 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-3">
        <div class="sm:col-span-1 content-center">
          <label for="file_ijazah" class="block leading-6 text-gray-600 text-sm font-medium font-sans mt-2">
            File Ijazah
          </label>
        </div>
        <div class="sm:col-span-1">
          <div class="mt-2">
            <div class="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
              <div class="text-center">
                <svg
                  v-if="!modelValue.file_ijazah"
                  class="mx-auto h-12 w-12 text-gray-300"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z"
                    clip-rule="evenodd"
                  />
                </svg>

                <div v-else class="thumbnail">
                  <img
                    :src="thumbnailUrlIjazah"
                    alt="Ijazah SMA/Sederajat"
                    class="max-w-full max-h-96 mx-auto mb-4"
                  />
                  <span>{{ modelValue.file_ijazah.name }}</span>
                </div>

                <div class="mt-4 flex text-sm leading-6 text-gray-600 justify-center">
                  <label
                    for="file-upload-ijazah"
                    class="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                  >
                    <span>Upload Ijazah SMA/Sederajat</span>
                    <input
                      id="file-upload-ijazah"
                      @change="onFileIjazahChange"
                      ref="modelValue.file_ijazah"
                      name="file-upload-ijazah"
                      type="file"
                      class="sr-only"
                    />
                  </label>
                </div>

                <p class="text-xs leading-5 text-gray-600">PNG, JPG max size 2MB</p>
                <p class="text-xs leading-5 text-red-600">*Pastikan foto terlihat jelas, tidak tertutup ataupun terpotong</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="mt-6 flex items-center justify-end gap-x-6">
    <button type="button" class="text-sm font-semibold leading-6 text-gray-900" @click="prev">kembali</button>
    <button
      @click="submit"
      class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
    >
      Submit
    </button>
  </div>

  <ModalError @close-modal="closeModal" :message="errorMessageFile" />
</template>

<script setup>
import { defineEmits, ref, defineProps } from "vue";
import ModalError from "./ModalError.vue";

const props = defineProps({
  modelValue: Object,
});
const thumbnailUrlIjazah = ref(null);
const errorMessageFile = ref(null);
const emit = defineEmits(["prev", "submit", "update:modelValue"]);

const updateField = (field, value) => {
  emit("update:modelValue", { ...props.modelValue, [field]: value });
};

const onFileIjazahChange = (event) => {
  const selectedFile = event.target.files[0];
  if (selectedFile) {
    validateFile(selectedFile);
  }
};

const validateFile = (file) => {
  const validMimeTypes = ["image/jpeg", "image/png", "application/pdf"];
  const validExtensions = [".jpg", ".jpeg", ".png", ".pdf"];
  const maxSize = 2 * 1024 * 1024; // 2MB

  const fileName = file.name.toLowerCase();

  const isValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
  const isValidMime = validMimeTypes.includes(file.type);

  if (!isValidMime || !isValidExtension) {
    errorMessageFile.value = "Hanya file JPG, PNG, atau PDF yang diperbolehkan.";
    updateField("file_ijazah", null);
    thumbnailUrlIjazah.value = null;
    return;
  }

  if (file.size > maxSize) {
    errorMessageFile.value = "Ukuran file tidak boleh lebih dari 2MB.";
    updateField("file_ijazah", null);
    thumbnailUrlIjazah.value = null;
    return;
  }

  errorMessageFile.value = null;
  updateField("file_ijazah", file);

  // Hanya buat thumbnail untuk gambar
  if (file.type.startsWith("image/")) {
    generateThumbnail(file);
  } else {
    thumbnailUrlIjazah.value = null;
  }
};

const generateThumbnail = (file) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    thumbnailUrlIjazah.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

const closeModal = () => {
  errorMessageFile.value = "";
};
const submit = () => {
  emit("submit");
};
const prev = () => {
  emit("prev");
};
</script>


<style></style>
